/**
 * Prohibited Words Library
 * 
 * Categorized word lists for content filtering across the platform.
 * Each category has a severity level and an array of terms.
 * 
 * MATCHING NOTES:
 * - The matcher engine normalizes leetspeak (@ → a, 0 → o, $ → s, etc.)
 * - Repeated characters are collapsed (niggger → niger)
 * - Diacritics/accents are stripped (é → e)
 * - Word boundary matching prevents false positives ("assassin" won't flag "ass")
 * - Multi-word phrases match with flexible whitespace
 * 
 * To add new words: add them to the appropriate category array below.
 * AVOID single common words that cause false positives (e.g., "child" alone).
 * Use specific phrases instead (e.g., "child porn", "child abuse").
 */

// =============================================================================
// Category: ILLEGAL CONTENT / EXPLOITATION (severity: critical)
// Covers: CSAM, trafficking, non-consent, illegal acts
// =============================================================================
const ILLEGAL_CONTENT = [
  // Child sexual abuse material
  'csam',
  'child porn',
  'child pornography',
  'child abuse',
  'child sex',
  'child nude',
  'child naked',
  'child exploit',
  'child erotica',
  'cp links',
  'cp content',
  'cp vid',
  'cp pic',
  'underage',
  'underage sex',
  'underage porn',
  'underage nude',
  'underage naked',
  'underage girl',
  'underage boy',
  'minor sex',
  'minor porn',
  'minor nude',
  'minor naked',
  'preteen',
  'pre-teen',
  'preteen sex',
  'preteen porn',
  'preteen nude',
  'preteen naked',
  'preteen model',
  'pedo',
  'pedophile',
  'pedophilia',
  'paedophile',
  'paedophilia',
  'jailbait',
  'lolicon',
  'shotacon',
  'toddlercon',
  'celebs',
  'celebrity porn',
  'celebrity nude',
  'celebrity leaked',
  'celebrity sex tape',
  'kiddie porn',
  'kiddy porn',
  'young porn',
  'teen porn',
  'teen nude',
  'barely legal',
  'kids',
  'children',

  // Epoch Req 3 exact meta-tag words (standalone)
  'animals',
  'celebrities',
  'celebrity',
  'illegal',

  // Trafficking & slavery
  'human trafficking',
  'sex trafficking',
  'sex slave',
  'sexual slavery',
  'forced prostitution',
  'forced labor',
  'buy a slave',
  'sell a slave',
  'slave trade',
  'slave auction',
  'organ harvesting',
  'organ trafficking',

  // Non-consensual
  'rape porn',
  'rape video',
  'rape fantasy',
  'forced sex',
  'non consensual',
  'nonconsensual',
  'without consent',
  'drugged sex',
  'chloroform sex',
  'revenge porn',
  'leaked nudes',
  'stolen nudes',
  'hidden camera sex',
  'spy cam sex',
  'voyeur porn',
  'voyeur',
  'public voyeur',
  'public',
  'creepshot',
  'upskirt',
  'rape',

  // Extreme illegal content
  'snuff',
  'snuff film',
  'crush fetish',
  'bestiality',
  'beastiality',
  'beast sex',
  'beast',
  'zoophilia',
  'zoofilia',
  'animal sex',
  'animal porn',
  'farm sex',
  'necrophilia',
  'necro porn',
  'incest',
  'incest porn',
  'incest sex',
  'real incest',
  'family sex',
  'nudist',
  'lolita',
  'peta',
  'peto',

  // Drug manufacturing / dealing
  'how to cook meth',
  'how to make meth',
  'buy drugs online',
  'sell drugs',
  'drug dealer',
  'buy cocaine',
  'buy heroin',
  'buy fentanyl',
  'darknet market',
  'dark web market',
  'buy counterfeit',
  'fake passport',
  'fake id for sale',
  'stolen credit card',
  'carding',
  'card dump',
  'fullz for sale',

  // Weapons / explosives
  'buy a gun illegally',
  'ghost gun',
  'unregistered firearm',
  'how to make a bomb',
  'pipe bomb instructions',
  'pressure cooker bomb',
  'improvised explosive',
  'ied instructions',
  '3d printed gun',
  'buy ammo no background',
];

// =============================================================================
// Category: HATE SPEECH / SLURS (severity: critical)
// Covers: Racial slurs, ethnic slurs, homophobic/transphobic slurs,
//         ableist slurs, religious hate, supremacy language
// =============================================================================
const HATE_SPEECH = [
  // Anti-Black slurs
  'nigger',
  'nigga',
  'nig nog',
  'coon',
  'darkie',
  'jiggaboo',
  'jigaboo',
  'spook',
  'porch monkey',
  'jungle bunny',
  'tar baby',
  'sambo',
  'golliwog',

  // Anti-Hispanic/Latino slurs
  'spic',
  'spick',
  'wetback',
  'beaner',
  'greaseball',
  'greaser',

  // Anti-Asian slurs
  'chink',
  'chinky',
  'gook',
  'slant eye',
  'zipperhead',
  'jap',
  'nip',

  // Anti-Middle Eastern / South Asian slurs
  'raghead',
  'towelhead',
  'sand nigger',
  'camel jockey',
  'hajji',
  'paki',
  'curry muncher',
  'dot head',

  // Anti-White slurs (for completeness — hate speech rules apply to all)
  'cracker',
  'honky',
  'redneck trash',
  'white trash',

  // Anti-Indigenous slurs
  'redskin',
  'prairie nigger',
  'wagon burner',

  // Antisemitic slurs & tropes
  'kike',
  'heeb',
  'hymie',
  'sheeny',
  'gas the jews',
  'oven dodger',
  'jewish conspiracy',
  'jewish plot',
  'zionist occupied',
  'protocols of zion',

  // Anti-Roma slurs
  'gypsy scum',

  // Homophobic slurs
  'faggot',
  'fag',
  'homo',
  'queer bash',
  'dyke',
  'lesbo',
  'pillow biter',
  'shirt lifter',
  'batty boy',
  'batty man',
  'sodomite',
  'conversion therapy',

  // Transphobic slurs
  'tranny',
  'shemale',
  'she male',
  'ladyboy',
  'trap',
  'troon',
  'gender freak',
  'man in a dress',

  // Ableist slurs
  'retard',
  'retarded',
  'tard',
  'mongo',
  'mongoloid',
  'spaz',
  'spastic',
  'cripple',
  'window licker',

  // Islamophobic
  'muzzie',
  'muzzrat',
  'goat fucker',
  'religion of peace',

  // Supremacy & organized hate
  'white power',
  'white supremacy',
  'white pride worldwide',
  'heil hitler',
  'sieg heil',
  'aryan nation',
  'aryan brotherhood',
  'blood and soil',
  'fourteen words',
  'white genocide',
  'race war',
  'day of the rope',
  'rahowa',
  'ethnic cleansing',
  'holocaust denial',
  'holohoax',
  'great replacement',
  'kill all',
  'death to all',
  'all must die',
  'subhuman',
  'untermensch',
  'mud race',
  'race traitor',
  'race mixing is',
  'miscegenation',
];

// =============================================================================
// Category: SCAM / SPAM / PHISHING (severity: high)
// Covers: Financial scams, phishing, social engineering
// =============================================================================
const SCAM_SPAM = [
  // Money transfer scams
  'send money to',
  'wire money to',
  'wire transfer to',
  'transfer funds to',
  'western union',
  'moneygram',
  'money order to',

  // Get rich quick
  'get rich quick',
  'get rich fast',
  'double your money',
  'triple your money',
  'guaranteed income',
  'guaranteed profit',
  'guaranteed returns',
  'risk free investment',
  'passive income secret',
  'make money fast',
  'earn thousands daily',
  'financial freedom secret',
  'mlm opportunity',
  'multi level marketing',
  'pyramid scheme',
  'ponzi',

  // Classic scam patterns
  'nigerian prince',
  'advance fee',
  'lottery winner',
  'you have won',
  'you are a winner',
  'claim your prize',
  'claim your reward',
  'claim your winnings',
  'inheritance from',
  'unclaimed funds',
  'beneficiary of',
  'million dollar',
  'billion dollar',
  'congratulations you won',
  'selected as winner',

  // Phishing / credential theft
  'bank account details',
  'bank account number',
  'routing number',
  'social security number',
  'ssn number',
  'credit card number',
  'card number and cvv',
  'pin number',
  'password reset link',
  'verify your account',
  'confirm your identity',
  'account suspended',
  'suspended account',
  'account locked',
  'unusual activity detected',
  'security alert click',
  'update your payment',
  'click here immediately',
  'act now or lose',
  'urgent action required',
  'your account will be closed',
  'verify your information',
  'confirm your details',
  'limited time only free',

  // Fake urgency / pressure
  'only 3 spots left',
  'offer expires today',
  'last chance to',
  'going fast act now',
  'dont miss out sign up',

  // Investment scams
  'forex signals',
  'binary options',
  'guaranteed trading',
  'insider trading tip',
  'pump and dump',
  'crypto pump',

  // Employment scams
  'work from home easy money',
  'no experience needed earn',
  'hiring now no interview',
  'paid daily just click',
];

// =============================================================================
// Category: OFF-PLATFORM SOLICITATION (severity: high)
// Covers: Competing platforms, payment apps, contact diversion,
//         meetup solicitation, external links for payment
// =============================================================================
const SOLICITATION = [
  // Payment app solicitation
  'cashapp',
  'cash app',
  'cashapp me',
  'my cashapp is',
  'venmo me',
  'my venmo',
  'my venmo is',
  'paypal me',
  'my paypal',
  'my paypal is',
  'zelle me',
  'my zelle',
  'my zelle is',
  'pay me on',
  'send me money on',
  'tip me on',
  'send to my',
  'google pay me',
  'apple pay me',
  'wise transfer',

  // Crypto solicitation
  'crypto wallet',
  'bitcoin address',
  'btc address',
  'eth address',
  'wallet address',
  'send btc',
  'send eth',
  'send crypto',
  'send usdt',
  'my wallet is',
  'donate crypto',
  'donate bitcoin',
  'metamask address',
  'trust wallet',

  // Competing platforms
  'my onlyfans',
  'my fansly',
  'my fanvue',
  'my manyvids',
  'my chaturbate',
  'my stripchat',
  'my cam4',
  'my bongacams',
  'my livejasmin',
  'my pornhub',
  'my xvideos',
  'subscribe on onlyfans',
  'follow me on onlyfans',
  'follow me on fansly',
  'join my onlyfans',
  'join my fansly',
  'link in bio onlyfans',
  'check my onlyfans',
  'check my fansly',
  'free trial onlyfans',
  'free trial fansly',
  'buy from my website',
  'buy on my site',
  'shop my store',
  'my gofundme',
  'my patreon',
  'my ko-fi',
  'my kofi',
  'my buymeacoffee',
  'my amazon wishlist',
  'my throne wishlist',

  // Messaging app diversion
  'dm me on telegram',
  'add me on telegram',
  'my telegram',
  'my telegram is',
  'dm me on signal',
  'add me on signal',
  'my signal',
  'find me on whatsapp',
  'add me on whatsapp',
  'my whatsapp',
  'my whatsapp is',
  'text me on whatsapp',
  'dm me on snapchat',
  'add me on snapchat',
  'my snapchat',
  'my snap is',
  'snap me at',
  'add me on discord',
  'my discord',
  'my discord is',
  'join my discord',
  'dm me on kik',
  'my kik',
  'my kik is',
  'add me on skype',
  'my skype',
  'dm me on instagram',
  'add me on instagram',
  'my insta is',
  'dm me on twitter',
  'my twitter is',
  'dm me on tiktok',
  'message me on line',
  'my wechat',
  'add me on wechat',

  // Phone / email diversion
  'text me at',
  'call me at',
  'my number is',
  'my phone number',
  'email me at',
  'my email is',
  'hit me up at',
  'hmu at',

  // In-person meetup solicitation
  'meet me outside',
  'meet in person',
  'meet up with me',
  'meetup irl',
  'lets meet up',
  'come see me',
  'i can visit you',
  'full service',
  'escort service',
  'incall',
  'outcall',
  'gfe',
  'girlfriend experience real',
  'rates and services',
  'hourly rate',
  'booking available',

  // Promotional spam
  'use code',
  'promo code',
  'discount code',
  'coupon code',
  'referral code',
  'affiliate link',
  'click my link',
  'link in bio',
  'check my linktree',
  'my linktree',
];

// =============================================================================
// Category: PLATFORM ABUSE / IMPERSONATION (severity: high)
// Covers: Admin/staff impersonation, phishing for credentials,
//         fake authority, social engineering
// =============================================================================
const PLATFORM_ABUSE = [
  // Admin/staff impersonation
  'i am admin',
  'i am an admin',
  'i am the admin',
  'i am a moderator',
  'i am a mod',
  'im an admin',
  'im a moderator',
  'official support',
  'official team',
  'official staff',
  'platform support team',
  'customer support team',
  'support representative',
  'support agent here',
  'moderator here',
  'admin here',
  'staff member',
  'staff here',
  'i work for the experience',
  'i work for vip experience',
  'i represent the platform',
  'on behalf of the platform',
  'platform management',
  'trust and safety team',
  'compliance team here',
  'verification team here',

  // Credential phishing
  'account verification needed',
  'verify your account now',
  'send me your password',
  'share your login',
  'share your credentials',
  'give me your password',
  'give me your login',
  'need your password',
  'need your login',
  'give me access',
  'send me your 2fa',
  'send me your code',
  'send me the otp',
  'what is your password',
  'what is your email password',
  'log into this link',

  // Fake warnings / authority
  'your account will be banned',
  'your account is being reviewed',
  'pay to avoid ban',
  'pay to keep account',
  'verification fee required',
  'upgrade fee required',
  'pay for verification',
  'send payment to verify',
  'your content is flagged pay',
  'your account needs upgrade',

  // Vote manipulation / engagement fraud
  'like for like',
  'follow for follow',
  'sub for sub',
  'l4l',
  'f4f',
  's4s',
  'follow me i follow back',
  'buy followers',
  'buy likes',
  'buy subscribers',
  'cheap followers',
  'boost your followers',
  'increase your fans',
];

// =============================================================================
// Category: VIOLENCE / THREATS (severity: critical)
// Covers: Death threats, harm threats, self-harm encouragement,
//         doxxing, swatting, terrorism, mass violence
// =============================================================================
const VIOLENCE_THREATS = [
  // Direct death threats
  'i will kill you',
  'i\'ll kill you',
  'ima kill you',
  'imma kill you',
  'gonna kill you',
  'going to kill you',
  'i will murder you',
  'i\'ll murder you',
  'i will end you',
  'you are dead',
  'you\'re dead',
  'youre dead meat',
  'you will die',
  'you\'re gonna die',
  'i will find you and kill',
  'watch your back',
  'sleep with one eye open',

  // Harm threats
  'i will hurt you',
  'i\'ll hurt you',
  'gonna beat you',
  'i will beat you up',
  'i\'ll beat the shit',
  'curb stomp',
  'i will stab you',
  'i\'ll shoot you',
  'put a bullet in',
  'catch a bullet',
  'catch these hands',
  'run you over',
  'burn your house',
  'burn down your',
  'acid attack',

  // Rape threats
  'i will rape you',
  'gonna rape you',
  'you deserve to be raped',
  'hope you get raped',

  // Self-harm encouragement
  'kill yourself',
  'kys',
  'go kill yourself',
  'you should kill yourself',
  'neck yourself',
  'hang yourself',
  'go hang yourself',
  'slit your wrists',
  'cut yourself',
  'drink bleach',
  'jump off a bridge',
  'go die',
  'just die',
  'hope you die',
  'wish you were dead',
  'the world is better without you',
  'do everyone a favor and die',
  'end your life',
  'end it all',
  'take your own life',

  // Threat keywords
  'death threat',
  'bomb threat',
  'hit list',
  'target list',
  'enemies list',
  'i have a gun',
  'i own a gun and',

  // Mass violence
  'shoot up',
  'shoot up a',
  'school shooting',
  'mass shooting',
  'mass murder',
  'mass killing',
  'going postal',
  'active shooter',

  // Terrorism
  'join isis',
  'join al qaeda',
  'allahu akbar kill',
  'jihad against',
  'holy war against',
  'terrorist attack',
  'glory to the caliphate',
  'infidels must die',
  'martyrdom operation',

  // Explosives / weapons intent
  'pipe bomb',
  'pressure cooker bomb',
  'molotov cocktail how',
  'car bomb',
  'mail bomb',
  'letter bomb',

  // Doxxing & swatting
  'dox',
  'doxx',
  'doxxing',
  'doxing',
  'swatting',
  'swat you',
  'swat your house',
  'i know where you live',
  'i know your address',
  'i have your address',
  'i found your address',
  'i know your real name',
  'posted your address',
  'your home address is',
  'sending people to your house',
  'i know your ip',
  'i traced your ip',
  'i have your ip address',

  // Stalking language
  'i am watching you',
  'i\'m watching you',
  'i follow you home',
  'i followed you',
  'i know where you work',
  'i know your school',
  'i saw you at',
  'i was outside your',

  // Animal cruelty
  'kill animals',
  'torture animals',
  'animal abuse',
  'animal cruelty',
  'hurt animals',
];

/**
 * All categories with metadata
 */
export const PROHIBITED_CATEGORIES = {
  ILLEGAL_CONTENT: {
    label: 'Illegal content',
    severity: 'critical',
    words: ILLEGAL_CONTENT,
  },
  HATE_SPEECH: {
    label: 'Hate speech',
    severity: 'critical',
    words: HATE_SPEECH,
  },
  VIOLENCE_THREATS: {
    label: 'Violent or threatening content',
    severity: 'critical',
    words: VIOLENCE_THREATS,
  },
  SCAM_SPAM: {
    label: 'Spam or scam content',
    severity: 'high',
    words: SCAM_SPAM,
  },
  SOLICITATION: {
    label: 'Off-platform solicitation',
    severity: 'high',
    words: SOLICITATION,
  },
  PLATFORM_ABUSE: {
    label: 'Impersonation or platform abuse',
    severity: 'high',
    words: PLATFORM_ABUSE,
  },
};

export default PROHIBITED_CATEGORIES;
